import React, { useEffect, useState } from "react";
import { useCompany } from "../../context/CompanyContext";
import {
  ensureCompanyTaxProfile,
  ensureTaxAccounts,
  getCompanyTaxProfile,
  saveCompanyTaxProfile,
} from "../../Services/taxService";
import "./tax.css";

const initialState = {
  legal_name: "",
  tin: "",
  registration_number: "",
  tax_office: "",
  vat_registered: true,
  wht_registered: true,
  cit_registered: true,
  dev_levy_applicable: true,
  base_currency: "NGN",
  vat_filing_frequency: "monthly",
  wht_filing_frequency: "monthly",
  cit_filing_frequency: "yearly",
  dev_levy_filing_frequency: "yearly",
  tax_year_start_month: 1,
};

export default function TaxConfiguration() {
  const { companyId } = useCompany();
  const [form, setForm] = useState(initialState);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    async function load() {
      if (!companyId) return;

      setLoading(true);
      setMsg("");

      try {
        await ensureCompanyTaxProfile(companyId);
        await ensureTaxAccounts(companyId);

        const profile = await getCompanyTaxProfile(companyId);
        if (profile) {
          setForm((prev) => ({ ...prev, ...profile }));
        }
      } catch (err) {
        console.error("Tax configuration load error:", err);
        setMsg(err.message || "Failed to load tax profile");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [companyId]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!companyId) return;
    setBusy(true);
    setMsg("");
    try {
      await saveCompanyTaxProfile(companyId, form);
      setMsg("Tax profile saved successfully");
    } catch (err) {
      setMsg(err.message || "Failed to save tax profile");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="tax-page">
        <div className="tax-empty">Loading tax configuration...</div>
      </div>
    );
  }

  return (
    <div className="tax-page">
      <div className="tax-page-header">
        <div>
          <h1>Tax Configuration</h1>
          <p>Set up tax identity and filing preferences for this company.</p>
        </div>
      </div>

      {msg ? <div className="tax-alert">{msg}</div> : null}

      <form className="tax-form tax-panel" onSubmit={handleSave}>
        <div className="tax-form-grid">
          <label>
            Legal Name
            <input
              name="legal_name"
              value={form.legal_name || ""}
              onChange={handleChange}
            />
          </label>

          <label>
            TIN
            <input name="tin" value={form.tin || ""} onChange={handleChange} />
          </label>

          <label>
            Registration Number
            <input
              name="registration_number"
              value={form.registration_number || ""}
              onChange={handleChange}
            />
          </label>

          <label>
            Tax Office
            <input
              name="tax_office"
              value={form.tax_office || ""}
              onChange={handleChange}
            />
          </label>

          <label>
            Base Currency
            <input
              name="base_currency"
              value={form.base_currency || "NGN"}
              onChange={handleChange}
            />
          </label>

          <label>
            Tax Year Start Month
            <input
              name="tax_year_start_month"
              type="number"
              min="1"
              max="12"
              value={form.tax_year_start_month || 1}
              onChange={handleChange}
            />
          </label>
        </div>

        <div className="tax-check-grid">
          <label>
            <input
              type="checkbox"
              name="vat_registered"
              checked={!!form.vat_registered}
              onChange={handleChange}
            />{" "}
            VAT Registered
          </label>
          <label>
            <input
              type="checkbox"
              name="wht_registered"
              checked={!!form.wht_registered}
              onChange={handleChange}
            />{" "}
            WHT Registered
          </label>
          <label>
            <input
              type="checkbox"
              name="cit_registered"
              checked={!!form.cit_registered}
              onChange={handleChange}
            />{" "}
            CIT Registered
          </label>
          <label>
            <input
              type="checkbox"
              name="dev_levy_applicable"
              checked={!!form.dev_levy_applicable}
              onChange={handleChange}
            />{" "}
            Development Levy Applicable
          </label>
        </div>

        <div className="tax-form-grid">
          <label>
            VAT Filing Frequency
            <select
              name="vat_filing_frequency"
              value={form.vat_filing_frequency}
              onChange={handleChange}
            >
              <option value="monthly">monthly</option>
            </select>
          </label>

          <label>
            WHT Filing Frequency
            <select
              name="wht_filing_frequency"
              value={form.wht_filing_frequency}
              onChange={handleChange}
            >
              <option value="monthly">monthly</option>
            </select>
          </label>

          <label>
            CIT Filing Frequency
            <select
              name="cit_filing_frequency"
              value={form.cit_filing_frequency}
              onChange={handleChange}
            >
              <option value="yearly">yearly</option>
            </select>
          </label>

          <label>
            Dev Levy Filing Frequency
            <select
              name="dev_levy_filing_frequency"
              value={form.dev_levy_filing_frequency}
              onChange={handleChange}
            >
              <option value="yearly">yearly</option>
            </select>
          </label>
        </div>

        <div className="tax-actions">
          <button type="submit" className="tax-btn primary" disabled={busy}>
            {busy ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </form>
    </div>
  );
}
